import React, { useState, useEffect } from 'react';
import { AlertTriangle, Calendar, BanknoteIcon, Briefcase, GraduationCap, Home, RefreshCcw, ExternalLink, Phone, Clock } from 'lucide-react';
import { Trans, useTranslation } from 'react-i18next';
import { storageController } from '../../controllers/StorageController';
import { useNavigate } from 'react-router-dom';
import { Logo } from '../Logo';
import Title from '../ui/Title';
import Description from '../ui/Description';
import ActionLink from '../ui/ActionLink';
import Button from '../ui/Button';
import ErrorMessage from '../ui/ErrorMessage';
import ScheduleCallForm from './ScheduleCallForm';
import { isBusinessHours } from '../../utils/businessHours';
import { format } from 'date-fns';
import { utcToZonedTime } from 'date-fns-tz';
import { getBrowserTimezone } from '../../utils/timezones';
import PhoneDialer from '../PhoneDialer';
import { settings } from '../../config/settings';
import { formatCurrency, getLoanPurpose, getEmploymentStatus, getPropertyStatus, getEducationLevel } from '../../utils/formatting';
import { TCPACompliance } from '../../utils/tcpaCompliance';
import { formController } from '../../controllers/FormController';

type ApplicationBlockedProps = {
  onInitiateCall: () => Promise<{ success: boolean; from?: string; error?: string }>;
  onStartNew?: () => void;
};

export default function ApplicationBlocked({ onInitiateCall, onStartNew }: ApplicationBlockedProps) {
  const { t, i18n } = useTranslation('terminal');
  const [showWarning, setShowWarning] = useState(false);
  const [showScheduleForm, setShowScheduleForm] = useState(false);
  const [isCallInitiated, setIsCallInitiated] = useState(false);
  const applicationData = storageController.getApplicationData();
  const daysRemaining = storageController.getBlockTimeRemaining();
  const isSuccessful = storageController.isApplicationSuccessful();
  const firstName = storageController.getContactFirstName() || '';
  const scheduledTime = storageController.getScheduledTime();
  const userPhone = applicationData?.formData?.phone || '';
  const navigate = useNavigate();
  
  // Check if within TCPA calling hours
  const isWithinCallHours = TCPACompliance.isWithinCallHours(new Date());
  
  const expirationDate = applicationData ? new Date(applicationData.timestamp + (30 * 24 * 60 * 60 * 1000)).toLocaleDateString(i18n.language, {
    month: 'long',
    day: 'numeric',
    year: 'numeric'
  }) : '';

  const formatAppointmentTime = (date: Date): string => {
    const timezone = getBrowserTimezone();
    const zonedDate = utcToZonedTime(date, timezone);
    return format(zonedDate, "EEEE, MMMM d 'at' h:mm a");
  };

  const handleClearStorage = () => {
    if (showWarning) {
      storageController.clearAll();
      onStartNew?.();
      navigate('/start');
    } else {
      setShowWarning(true);
    }
  };

  const handleScheduleCall = async (selectedTime: Date) => {
    await formController.scheduleAppointment(selectedTime);
    storageController.setScheduledTime(selectedTime);
    setShowScheduleForm(false);
  };

  const handleCallInitiated = () => {
    setIsCallInitiated(true);
  };

  if (applicationData?.status === 'unsuccessful') {
    return (
      <div id="blocked-unsuccessful" className="text-center space-y-6">
        <div className="w-16 h-16 bg-red-50 rounded-full flex items-center justify-center mx-auto">
          <AlertTriangle className="w-8 h-8 text-red-600" />
        </div>

        <div>
          <Title 
            id="blocked-unsuccessful-title"
            name="blocked-unsuccessful-title"
            as="h1" 
            className="mb-2"
          >
            {t('blocked.unsuccessfulTitle')}
          </Title>
          <Description
            id="blocked-unsuccessful-description"
            name="blocked-unsuccessful-description"
          >
            {t('blocked.unsuccessfulDescription', { date: expirationDate })}
          </Description>
        </div>

        <div className="max-w-xs mx-auto bg-white rounded-lg shadow-md p-4">
          <a 
            href="https://fiona.com/partner/symple-lending-loans/loans"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center justify-center gap-2 w-full py-3 px-4 bg-[#212d52] text-white rounded-lg hover:bg-[#1a2441] transition-colors font-semibold text-lg group"
            id="blocked-unsuccessful-alternative"
            data-name="blocked-unsuccessful-alternative"
          >
            {t('blocked.checkMoneyLionOffers')}
            <ExternalLink className="w-5 h-5 transition-transform group-hover:scale-110" />
          </a>
        </div>
      </div>
    );
  }

  // Show scheduler if requested
  if (showScheduleForm) {
    return (
      <div id="blocked-schedule" className="space-y-6">
        <ScheduleCallForm 
          firstName={firstName} 
          phoneNumber={userPhone}
          onSchedule={handleScheduleCall} 
        />
        <Button
          id="blocked-schedule-back"
          name="blocked-schedule-back"
          onClick={() => setShowScheduleForm(false)}
          variant="outline"
          size="lg"
          fullWidth
        >
          {t('blocked.backToContactOptions')}
        </Button>
      </div>
    );
  }

  return (
    <div id="blocked-application" className="text-center space-y-6">
      <div className="w-16 h-16 bg-yellow-50 rounded-full flex items-center justify-center mx-auto">
        <AlertTriangle className="w-8 h-8 text-yellow-600" />
      </div>

      <div>
        <Title 
          id="blocked-title"
          name="blocked-title"
          as="h2" 
          className="mb-2"
        >
          {t('blocked.alreadySubmittedTitle')}
        </Title>
      </div>

      {isSuccessful && (
        <div className="w-full md:w-2/3 mx-auto">
          <div className="bg-white rounded-lg shadow-md p-6 space-y-6">
            {scheduledTime ? (
              <>
                <div className="flex items-center justify-center gap-2 text-[#b3905e]">
                  <Calendar className="w-5 h-5" />
                  <span className="font-medium">{t('blocked.appointmentScheduled')}</span>
                </div>
                <Description 
                  id="blocked-scheduled-description"
                  name="blocked-scheduled-description"
                  className="text-center"
                >
                  {t('blocked.callbackScheduledFor')}
                  <div className="font-medium mt-1">{formatAppointmentTime(scheduledTime)}</div>
                </Description>
                <Description 
                  id="blocked-immediate-assistance"
                  name="blocked-immediate-assistance"
                  className="text-center"
                >
                  {t('blocked.immediateAssistance')}
                </Description>
                <Button
                  id="blocked-call-support"
                  name="blocked-call-support"
                  variant="secondary"
                  size="lg"
                  icon={<Phone className="w-5 h-5" />}
                  phoneNumber={settings.phone.supportRaw}
                >
                  {settings.phone.support}
                </Button>
              </>
            ) : (
              <div className="space-y-4">
                <div className="text-center">
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">
                    {t('blocked.contactSpecialistsHeading')}
                  </h3>
                  <p className="text-gray-600">
                    {t('blocked.chooseHowToConnect')}
                  </p>
                </div>

                <div className="grid gap-3">
                  {/* Only show call options during TCPA hours */}
                  {isWithinCallHours ? (
                    <>
                      {/* Call Me Now Button */}
                      <div className="bg-white rounded-lg border border-gray-200">
                        <PhoneDialer 
                          phoneNumber={userPhone} 
                          fallbackNumber={settings.phone.support}
                          autoTrigger={false}
                          onCallInitiated={handleCallInitiated}
                          onInitiateCall={onInitiateCall}
                        />
                      </div>

                      {/* Only show these buttons if call is not initiated */}
                      {!isCallInitiated && (
                        <>
                          {/* Call Now Button */}
                          <Button
                            id="blocked-call-now"
                            name="blocked-call-now"
                            variant="secondary"
                            size="lg"
                            icon={<Phone className="w-5 h-5" />}
                            phoneNumber={settings.phone.supportRaw}
                          >
                            {settings.phone.support}
                          </Button>

                          {/* Schedule Appointment Button */}
                          <Button
                            id="blocked-schedule-appointment"
                            name="blocked-schedule-appointment"
                            onClick={() => setShowScheduleForm(true)}
                            variant="outline"
                            size="lg"
                            icon={<Calendar className="w-5 h-5" />}
                          >
                            {t('blocked.scheduleAppointment')}
                          </Button>
                        </>
                      )}
                    </>
                  ) : (
                    <>
                      {/* Call Now Button */}
                      <Button
                        id="blocked-call-now"
                        name="blocked-call-now"
                        variant="secondary"
                        size="lg"
                        icon={<Phone className="w-5 h-5" />}
                        phoneNumber={settings.phone.supportRaw}
                      >
                        {settings.phone.support}
                      </Button>
                      {/* Schedule Appointment Button */}
                      <Button
                        id="blocked-schedule-only"
                        name="blocked-schedule-only"
                        onClick={() => setShowScheduleForm(true)}
                        variant="primary"
                        size="lg"
                        icon={<Calendar className="w-5 h-5" />}
                      >
                        {t('blocked.scheduleAppointment')}
                      </Button>
                    </>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      )}
      
      {applicationData?.formData && (
        applicationData.formData.loanAmount ||
        applicationData.formData.loanPurpose ||
        applicationData.formData.employmentStatus ||
        applicationData.formData.propertyStatus
      ) && (
        <div id="blocked-details" className="bg-gray-50 rounded-lg p-6 text-left max-w-md mx-auto">
          <h3 className="font-semibold text-gray-900 mb-4">{t('blocked.previousDetailsHeading')}</h3>
          
          <div className="space-y-4">
            {applicationData?.formData?.loanAmount && (
              <div className="flex items-center gap-3">
                <BanknoteIcon className="w-5 h-5 text-[#212d52]" />
                <div>
                  <div className="text-sm text-gray-600">{t('blocked.loanAmountLabel')}</div>
                  <div className="font-medium text-gray-900">
                    {formatCurrency(applicationData.formData.loanAmount)}
                  </div>
                </div>
              </div>
            )}

            {applicationData.formData.loanPurpose && (
              <div className="flex items-center gap-3">
                <BanknoteIcon className="w-5 h-5 text-[#212d52]" />
                <div>
                  <div className="text-sm text-gray-600">{t('blocked.loanPurposeLabel')}</div>
                  <div className="font-medium text-gray-900">
                    {getLoanPurpose(applicationData.formData.loanPurpose)}
                  </div>
                </div>
              </div>
            )}

            {applicationData.formData.employmentStatus && (
              <div className="flex items-center gap-3">
                <Briefcase className="w-5 h-5 text-[#212d52]" />
                <div>
                  <div className="text-sm text-gray-600">{t('blocked.employmentStatusLabel')}</div>
                  <div className="font-medium text-gray-900">
                    {getEmploymentStatus(applicationData.formData.employmentStatus)}
                  </div>
                </div>
              </div>
            )}

            {applicationData.formData.propertyStatus && (
              <div className="flex items-center gap-3">
                <Home className="w-5 h-5 text-[#212d52]" />
                <div>
                  <div className="text-sm text-gray-600">{t('blocked.propertyStatusLabel')}</div>
                  <div className="font-medium text-gray-900">
                    {getPropertyStatus(applicationData.formData.propertyStatus)}
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      <div className="bg-gray-50 p-4 rounded-lg inline-flex items-center gap-3 mx-auto">
        <Calendar className="w-5 h-5 text-[#212d52]" />
        <span className="text-gray-700">
          <Trans
            i18nKey="blocked.applyAgainAfter"
            ns="terminal"
            values={{ date: expirationDate }}
            components={{ dateSpan: <span className="font-medium" /> }}
          />
        </span>
      </div>

      {showWarning ? (
        <div className="space-y-4">
          <ErrorMessage
            title={t('blocked.warningTitle')}
            message={t('blocked.warningMessage')}
            severity="error"
            align="center"
          />
          <div className="flex gap-3 justify-center">
            <Button
              id="blocked-confirm-reset"
              name="blocked-confirm-reset"
              onClick={handleClearStorage}
              variant="danger"
              icon={<RefreshCcw className="w-4 h-4" />}
            >
              {t('blocked.confirmReset')}
            </Button>
            <Button
              id="blocked-cancel-reset"
              name="blocked-cancel-reset"
              onClick={() => setShowWarning(false)}
              variant="muted"
            >
              {t('blocked.cancelReset')}
            </Button>
          </div>
        </div>
      ) : (
        <div className="flex justify-center">
          <ActionLink
            id="blocked-start-new"
            name="blocked-start-new"
            onClick={handleClearStorage}
            icon={RefreshCcw}
            align="center"
            className="block"
          >
            {t('blocked.startNew')}
          </ActionLink>
        </div>
      )}
    </div>
  );
}